// pages/Services.js
import React, { useState } from 'react';
import { Container, Typography, Grid, Box, Button, useTheme, useMediaQuery, Chip } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import ServiceCard from '../components/ServiceCard';
import { Code, LaptopMac, ConnectWithoutContact, Security, Palette, School, Star, Rocket, TrendingUp } from '@mui/icons-material';
import ServiceCategories from '../components/ServiceCategories';

const Services = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [hoveredCard, setHoveredCard] = useState(null);

  const services = [
    {
      title: 'Software Development',
      description: 'Custom software solutions designed to streamline business processes and drive measurable growth.',
      icon: Code,
      features: ['Web Applications', 'Mobile Applications', 'Enterprise Software', 'API Development & Integration', 'Cloud Solutions', 'DevOps Implementation'],
      detailedDescription: 'We deliver scalable, secure, and modern software tailored to your business needs. From full-stack development to mobile apps and API integrations, we ensure efficient operations and sustainable growth. Our agile methodology ensures timely delivery and continuous improvement.',
      color: '#a8dadc',
      gradient: 'linear-gradient(135deg, #a8dadc 0%, #457b9d 100%)',
      popular: true
    },
    {
      title: 'Web Development',
      description: 'Building responsive, high-performance websites and web applications tailored to your brand.',
      icon: LaptopMac,
      features: ['Responsive Web Design', 'Web Applications', 'UI/UX Optimization', 'Content Management Systems', 'E-commerce Solutions', 'Progressive Web Apps'],
      detailedDescription: 'Our team creates fast, secure, and intuitive websites and portals. We focus on modern design, usability, and performance to enhance engagement and deliver seamless user experiences. Every project is optimized for speed, accessibility, and search engine visibility.',
      color: '#ffe5b4',
      gradient: 'linear-gradient(135deg, #ffe5b4 0%, #ffb347 100%)'
    },
    {
      title: 'IT Consulting',
      description: 'Expert advisory to optimize IT infrastructure, enhance security, and drive digital transformation.',
      icon: ConnectWithoutContact,
      features: ['IT Strategy Planning', 'System Architecture', 'Digital Transformation', 'Cybersecurity & Compliance', 'Infrastructure Optimization', 'Technology Roadmaps'],
      detailedDescription: 'Our consultants provide strategic guidance to align technology with business goals. We help modernize IT systems, improve security, and implement processes that maximize efficiency and growth potential. Comprehensive assessments and actionable roadmaps included.',
      color: '#f8c8dc',
      gradient: 'linear-gradient(135deg, #f8c8dc 0%, #e75480 100%)'
    },
    {
      title: 'Web Hosting & Security',
      description: 'Reliable and secure hosting solutions for uninterrupted digital presence and business continuity.',
      icon: Security,
      features: ['Cloud Hosting', 'VPS Hosting', 'Database Management', '24/7 Monitoring & Support', 'SSL Certificates', 'Backup Solutions'],
      detailedDescription: 'We provide scalable hosting solutions with high availability and robust security. Automated backups, disaster recovery, and performance optimization ensure your websites and applications run smoothly. Enterprise-grade security protocols protect your digital assets.',
      color: '#c6f1d6',
      gradient: 'linear-gradient(135deg, #c6f1d6 0%, #4caf50 100%)',
      featured: true
    },
    {
      title: 'Branding & Design',
      description: 'Creative visual solutions to build strong brand identities and engaging user experiences.',
      icon: Palette,
      features: ['Logo & Identity Design', 'UI/UX Design', 'Marketing Collateral', 'Digital Graphics & Assets', 'Brand Guidelines', 'Visual Strategy'],
      detailedDescription: 'We craft cohesive brand systems and visually engaging experiences. From logos to UI/UX and marketing materials, our designs enhance brand recognition and support business objectives. Comprehensive brand guidelines ensure consistency across all touchpoints.',
      color: '#d9c6f5',
      gradient: 'linear-gradient(135deg, #d9c6f5 0%, #9c27b0 100%)'
    },
    {
      title: 'Internship & Training',
      description: 'Structured programs for skill development, professional training, and future talent acquisition.',
      icon: School,
      features: ['Technical Workshops', 'Online Courses', 'Skill Development Programs', 'Certifications & Mentorship', 'Hands-on Projects', 'Career Guidance'],
      detailedDescription: 'We provide hands-on training, workshops, and internships to upskill employees and students. Our programs foster practical experience, professional growth, and help identify high-potential talent. Customized learning paths for individuals and teams.',
      color: '#fbe8c6',
      gradient: 'linear-gradient(135deg, #fbe8c6 0%, #ff9800 100%)'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
        duration: 1
      }
    }
  };

  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 80,
      scale: 0.9,
      rotateX: -15
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      rotateX: 0,
      transition: {
        duration: 0.8,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  const headerVariants = {
    hidden: { opacity: 0, y: -80 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 1.2,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  const floatingVariants = {
    animate: {
      y: [0, -20, 0],
      transition: {
        duration: 4,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0a1128 0%, #0f1a3a 25%, #1a2342 50%, #0f1a3a 75%, #0a1128 100%)',
        backgroundSize: '400% 400%',
        py: 8,
        position: 'relative',
        overflow: 'hidden',
        animation: 'gradientShift 15s ease infinite',
        '@keyframes gradientShift': {
          '0%': { backgroundPosition: '0% 50%' },
          '50%': { backgroundPosition: '100% 50%' },
          '100%': { backgroundPosition: '0% 50%' }
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 30%, rgba(139, 0, 0, 0.2) 0%, transparent 25%),
            radial-gradient(circle at 80% 70%, rgba(139, 0, 0, 0.15) 0%, transparent 25%),
            radial-gradient(circle at 40% 80%, rgba(139, 0, 0, 0.1) 0%, transparent 25%),
            linear-gradient(45deg, rgba(139, 0, 0, 0.05) 0%, transparent 50%)
          `,
          pointerEvents: 'none',
        }
      }}
    >

      {/* Animated Background Elements */}
      <motion.div
        variants={floatingVariants}
        animate="animate"
        style={{
          position: 'absolute',
          top: '15%',
          left: '8%',
          width: '120px',
          height: '120px',
          background: 'radial-gradient(circle, rgba(139, 0, 0, 0.15) 0%, transparent 70%)',
          borderRadius: '50%',
          filter: 'blur(15px)',
        }}
      />
      <motion.div
        variants={floatingVariants}
        animate="animate"
        transition={{ delay: 1 }}
        style={{
          position: 'absolute',
          bottom: '20%',
          right: '10%',
          width: '180px',
          height: '180px',
          background: 'radial-gradient(circle, rgba(139, 0, 0, 0.1) 0%, transparent 70%)',
          borderRadius: '50%',
          filter: 'blur(20px)',
        }}
      />
      <motion.div
        variants={floatingVariants}
        animate="animate"
        transition={{ delay: 2 }}
        style={{
          position: 'absolute',
          top: '60%',
          left: '70%',
          width: '80px',
          height: '80px',
          background: 'radial-gradient(circle, rgba(139, 0, 0, 0.08) 0%, transparent 70%)',
          borderRadius: '50%',
          filter: 'blur(10px)',
        }}
      />

      {/* Floating Particles */}
      {[...Array(15)].map((_, i) => (
        <motion.div
          key={i}
          style={{
            position: 'absolute',
            width: Math.random() * 4 + 2,
            height: Math.random() * 4 + 2,
            background: `rgba(139, 0, 0, ${Math.random() * 0.3 + 0.1})`,
            borderRadius: '50%',
            top: `${Math.random() * 100}%`,
            left: `${Math.random() * 100}%`,
          }}
          animate={{
            y: [0, -100, 0],
            x: [0, Math.random() * 50 - 25, 0],
            opacity: [0, 1, 0],
          }}
          transition={{
            duration: Math.random() * 10 + 10,
            repeat: Infinity,
            delay: Math.random() * 5,
          }}
        />
      ))}

      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Header Section */}
        <motion.div
          variants={headerVariants}
          initial="hidden"
          animate="visible"
        >
          <Box sx={{ textAlign: 'center', mb: 10 }}>
            <motion.div
              initial={{ scale: 0.8, rotateY: 180 }}
              animate={{ scale: 1, rotateY: 0 }}
              transition={{ duration: 1, delay: 0.3, ease: "easeOut" }}
            >
              <Typography
                variant="h1"
                component="h1"
                gutterBottom
                sx={{
                  fontWeight: '900',
                  mb: 3,
                  background: 'linear-gradient(45deg, #8b0000 0%, #ff4d4d 25%, #ff6666 50%, #ff4d4d 75%, #8b0000 100%)',
                  backgroundSize: '200% auto',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  color: 'transparent',
                  animation: 'textShine 3s ease-in-out infinite',
                  fontSize: { xs: '2.8rem', md: '4.5rem', lg: '5.5rem' },
                  textShadow: '0 8px 40px rgba(139, 0, 0, 0.5)',
                  letterSpacing: { xs: '-0.01em', md: '-0.03em' },
                  lineHeight: 1,
                  '@keyframes textShine': {
                    '0%': { backgroundPosition: '0% center' },
                    '50%': { backgroundPosition: '200% center' },
                    '100%': { backgroundPosition: '0% center' }
                  }
                }}
              >
                Our Services
              </Typography>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              <Typography
                variant="h4"
                component="p"
                sx={{
                  mb: 4,
                  color: 'rgba(255,255,255,0.95)',
                  fontSize: { xs: '1.2rem', md: '1.6rem' },
                  fontWeight: '300',
                  maxWidth: '800px',
                  margin: '0 auto',
                  lineHeight: 1.6,
                  textShadow: '0 4px 20px rgba(0, 0, 0, 0.4)',
                  background: 'linear-gradient(90deg, #ffffff, #e0e0e0, #ffffff)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  color: 'transparent',
                  backgroundSize: '200% auto',
                  animation: 'textShine 4s ease-in-out infinite',
                }}
              >
                Comprehensive technology solutions to transform your business and drive digital innovation
              </Typography>
              <Typography
                sx={{
                  mt: 1.5,
                  fontSize: "0.85rem",
                  color: "rgba(255,255,255,0.65)",
                  fontStyle: "italic",
                  textAlign: "center",
                  letterSpacing: "0.04em"
                }}
              >
                “அறம் செய விரும்பு”
              </Typography>

              <Typography
                sx={{
                  mt: 0.4,
                  fontSize: "0.75rem",
                  color: "rgba(255,255,255,0.55)",
                  textAlign: "center"
                }}
              >
                “Choose service guided by righteousness.”
              </Typography>

            </motion.div>

            <motion.div
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: '200px', opacity: 1 }}
              transition={{ duration: 1, delay: 0.8, ease: "easeOut" }}
              style={{ margin: '0 auto' }}
            >
              <Box
                sx={{
                  height: '6px',
                  background: 'linear-gradient(90deg, transparent, #8b0000, #ff4d4d, #ff6666, #ff4d4d, #8b0000, transparent)',
                  borderRadius: '3px',
                  marginTop: 3,
                  boxShadow: '0 0 20px rgba(139, 0, 0, 0.5)',
                  position: 'relative',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(90deg, transparent, #ffffff, transparent)',
                    animation: 'shine 2s ease-in-out infinite',
                    '@keyframes shine': {
                      '0%': { transform: 'translateX(-100%)' },
                      '100%': { transform: 'translateX(100%)' }
                    }
                  }
                }}
              />
            </motion.div>

            {/* Stats Bar */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1 }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4, mt: 6, flexWrap: 'wrap' }}>
                {[
                  { icon: Rocket, label: 'Projects Completed', value: 'Transform.' },
                  { icon: TrendingUp, label: 'Happy Clients', value: 'Growth.' },
                  { icon: Star, label: 'Success Rate', value: 'Succeed.' }
                ].map((stat, index) => (
                  <motion.div
                    key={index}
                    whileHover={{ scale: 1.1, y: -5 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <Box sx={{ textAlign: 'center', px: 3 }}>
                      <stat.icon sx={{ fontSize: 40, color: '#ff4d4d', mb: 1 }} />
                      <Typography variant="h4" sx={{ color: 'white', fontWeight: '800', mb: 0.5 }}>
                        {stat.value}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: '300' }}>
                        {stat.label}
                      </Typography>
                    </Box>
                  </motion.div>
                ))}
              </Box>
            </motion.div>
          </Box>
        </motion.div>

        {/* Services Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
        >
          <Grid container spacing={4} justifyContent="center">
            {services.map((service, index) => (
              <Grid
                item
                xs={12}
                sm={6}
                lg={4}
                key={index}
                sx={{
                  display: 'flex',
                  justifyContent: 'center'
                }}
              >
                <motion.div
                  variants={itemVariants}
                  style={{ width: '100%', maxWidth: '450px' }}
                  whileHover={{
                    y: -15,
                    scale: 1.02,
                    transition: {
                      type: "spring",
                      stiffness: 300,
                      duration: 0.3
                    }
                  }}
                  onHoverStart={() => setHoveredCard(index)}
                  onHoverEnd={() => setHoveredCard(null)}
                >
                  <ServiceCard
                    service={service}
                    index={index}
                    isHovered={hoveredCard === index}
                  />
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </motion.div>
<motion.div>
  <ServiceCategories/>
</motion.div>
      </Container>
    </Box>
  );
};

export default Services;